import React, { useState, useEffect } from "react";
import ApiConfig from "../common/config/ApiConfig";
import { motion } from "framer-motion";
import moment from "moment";
import "moment/locale/th";
import "./hanoiroundselect.css";
import { Link, useLocation } from "react-router-dom";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DoNotDisturbIcon from "@mui/icons-material/DoNotDisturb";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import HourglassBottomIcon from "@mui/icons-material/HourglassBottom";
import { useTranslation } from "react-i18next";

// The round is already known to be open (round.status), so the close time
// ("HH:mm") is assumed to still be ahead today; no day-of-week/month
// evaluation needed.
function getCountdown(endTime, now) {
  if (!endTime) return null;
  const [hours, minutes] = endTime.split(":").map(Number);
  const target = moment(now).set({
    hours,
    minutes,
    seconds: 0,
    milliseconds: 0,
  });
  const duration = moment.duration(Math.max(target.diff(now), 0));
  const hh = String(Math.floor(duration.asHours())).padStart(2, "0");
  const mm = String(duration.minutes()).padStart(2, "0");
  const ss = String(duration.seconds()).padStart(2, "0");
  return `${hh}:${mm}:${ss}`;
}

function Hanoiroundselect(props) {
  const { t } = useTranslation();
  const location = useLocation();
  const lottoTypeId = location.state?.id;
  const user = localStorage.getItem("user");

  const [lottoType, setLottoType] = useState(null);
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    ApiConfig.post("/getLottoTypeSchedule", { memberuser: user }).then(
      (res) => {
        if (res.resCode === 200) {
          const item =
            res.data.find((entry) => entry.id === lottoTypeId) ||
            res.data.find((entry) => entry.type === "hanoy_PhuQua");
          setLottoType(item || null);
        }
      },
    );
    // eslint-disable-next-line
  }, [lottoTypeId]);

  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  const rounds = lottoType?.rounds || [];

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      style={{ display: "flex", flexDirection: "column" }}
    >
      <div className="flex flex-col justify-center">
        <div className="w-[100%] flex flex-row h-[44px] p-2 items-center bg-gradient-to-r from-brand-blue-800 via-brand-blue-700 to-brand-blue-800 border-b border-brand-gold-300/40 space-x-2 rounded-t-lg shadow-card">
          <Link
            to="/dashboard"
            className="text-center bg-brand-blue-900/70 border border-brand-gold-300/60 rounded-full shadow-md hover:bg-brand-blue-900 transition-colors px-3 py-1"
          >
            <span className="text-brand-gold-200 font-bold text-sm">
              {t("hanoiRoundSelect.back")}
            </span>
          </Link>
          <div className="bg-white/95 rounded-full shadow-sm px-3 py-1">
            <span className="text-brand-blue-700 font-bold text-sm">
              {t("hanoiRoundSelect.title")}
            </span>
          </div>
        </div>

        <div className="flex flex-wrap gap-3 bg-gradient-to-br from-brand-blue-900 via-brand-blue-800 to-brand-blue-900 border-[1px] border-brand-gold-300/30 p-4 w-[100%] h-[auto] shadow-inner">
          {rounds.map((round) => {
            const endTime = round.round_date?.[0]?.end_time;
            const isOpen = round.status;
            const countdown = isOpen ? getCountdown(endTime, now) : null;

            const cardBody = (
              <div
                className={
                  isOpen
                    ? "flex flex-col w-full sm:w-[220px] rounded-card overflow-hidden bg-gradient-to-br from-brand-blue-900 via-brand-blue-800 to-brand-blue-900 shadow-cardLight hover:shadow-card hover:-translate-y-1 transition-all duration-200 border-2 border-brand-gold-300"
                    : "flex flex-col w-full sm:w-[220px] rounded-card overflow-hidden bg-white shadow-sm border border-slate-200 opacity-60"
                }
              >
                <div
                  className={
                    isOpen
                      ? "flex flex-row items-center justify-between px-3 py-2 bg-gradient-to-r from-brand-blue-600 to-brand-blue-800"
                      : "flex flex-row items-center justify-between px-3 py-2 bg-slate-400"
                  }
                >
                  <span className="text-brand-gold-200 font-bold text-sm">
                    {t("hanoiRoundSelect.roundLabel", { round: round.round })}
                  </span>
                  <img
                    src="/images/hanoilogo.png"
                    alt={t("hanoiRoundSelect.title")}
                    className="w-6 h-6 rounded-full bg-white p-0.5"
                  />
                </div>

                <div className="flex flex-col p-3 gap-2">
                  <div
                    className={
                      isOpen
                        ? "flex items-center gap-1 text-white/70 text-xs"
                        : "flex items-center gap-1 text-slate-500 text-xs"
                    }
                  >
                    <AccessTimeIcon fontSize="inherit" />
                    <span>
                      {t("hanoiRoundSelect.closeAt")} {endTime || "-"}
                    </span>
                  </div>

                  <div
                    className={
                      isOpen
                        ? "flex items-center justify-center gap-1 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full py-1.5 font-bold text-sm"
                        : "flex items-center justify-center gap-1 bg-slate-100 text-slate-500 border border-slate-200 rounded-full py-1.5 font-bold text-sm"
                    }
                  >
                    {isOpen ? (
                      <>
                        <CheckCircleIcon fontSize="small" />
                        {t("hanoiRoundSelect.open")}
                      </>
                    ) : (
                      <>
                        <DoNotDisturbIcon fontSize="small" />
                        {t("hanoiRoundSelect.closed")}
                      </>
                    )}
                  </div>

                  {isOpen && countdown && (
                    <div className="flex items-center justify-center gap-1 text-brand-gold-300 text-xs font-semibold">
                      <HourglassBottomIcon fontSize="inherit" />
                      <span>
                        {t("hanoiRoundSelect.timeRemaining")} {countdown}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            );

            return isOpen ? (
              <Link
                key={round.id}
                to={`/dashboard/hanoi/${round.round}`}
                state={{ id: lottoTypeId }}
                className="w-full sm:w-auto"
              >
                {cardBody}
              </Link>
            ) : (
              <div key={round.id} className="w-full sm:w-auto">
                {cardBody}
              </div>
            );
          })}
        </div>
      </div>
    </motion.div>
  );
}

export default Hanoiroundselect;
